// Shared popover positioning math (BL-1066; horizontal half added by BUG-1625).
// Extracted so the `Select` primitive (packages/ui) and admin's portaled
// dropdowns compute a popover's viewport-fixed box from ONE place instead of
// each carrying its own copy of the same arithmetic.
//
// Pure function — no I/O, no React. Rendering the popover in a portal with the
// returned `position: fixed` box is what stops an overflow-clipping ancestor
// (e.g. a modal body with `overflow-y: auto`) from cutting the dropdown off
// (BUG-988); the flip-up branch keeps the menu off the bottom of the screen
// (BUG-1086); and the horizontal clamp keeps it off both side edges (BUG-1625).
//
// THE HORIZONTAL HALF IS WHY THIS FILE EXISTS IN ITS CURRENT SHAPE. Until
// BUG-1625 it computed the vertical axis only and returned `left =
// triggerRect.left` — no clamp, no width cap — so every consumer had to defend
// its own side edges, and they did it inconsistently: `AnchoredPopover` clamped
// both (BL-1441), `DateTimePicker` flipped left when it did not fit, and
// `StatusTransitionMenu` clamped only the RIGHT. That last gap is the reported
// bug: its 384px panel on a 390px phone is 26px wider than the 358px safe area,
// so anchoring its right edge to the trigger pushed its left edge off screen and
// every section heading lost its first character.
//
// That width is why `maxWidth` is part of the contract rather than a caller's
// business: a panel wider than the safe area has to be NARROWED as well as
// placed, and a consumer that ignores `maxWidth` can still paint past the edge
// it was correctly positioned inside.
//
// The old header said this helper was "deliberately NOT used by
// StatusTransitionMenu, which also anchors LEFT/RIGHT". That carve-out is gone —
// anchoring is now `align`, and folding it in is what removed the divergence
// rather than over-generalizing it.

/** Distance kept clear between a popover and each side edge of the viewport, px. */
export const POPOVER_SAFE_ZONE = 16;

/** A trigger-relative, viewport-fixed box for a portaled popover. */
export interface PopoverBox {
    /**
     * Left edge, clamped so the panel keeps `safeZone` clear of both side edges.
     * Always set — a consumer positions with `left` + `maxWidth` and never needs
     * to set `right` itself.
     */
    left: number;
    /** Trigger width, rounded to an integer. */
    width: number;
    /**
     * How much room the panel has: from `left` to the right safe edge. Apply it
     * as the panel's `max-width` — `left` only keeps the panel on screen for a
     * panel that respects this.
     *
     * Deliberately "room available" rather than "min(panelWidth, safe area)". A
     * content-sized menu (`Select` with `menuWidth: 'auto'`) has no natural width
     * to cap against before it renders, and capping it at its trigger's width
     * would defeat the mode; measuring the room instead bounds it correctly
     * without needing to know. A panel that also has its OWN ceiling intersects
     * the two — `Math.min(MY_MAX, box.maxWidth)` — rather than replacing one with
     * the other.
     */
    maxWidth: number;
    /** Set when the popover anchors BELOW the trigger (the default). */
    top?: number;
    /** Set when the popover is FLIPPED above the trigger (anchors its bottom edge). */
    bottom?: number;
    /**
     * Vertical room available in the chosen direction (px). Callers that clamp a
     * scroll list to the viewport (e.g. `Select`) subtract their own chrome from
     * this; callers that don't (e.g. `SearchableSelect`) ignore it.
     */
    availableHeight: number;
}

/** Inputs to {@link computePopoverBox}. */
export interface ComputePopoverBoxInput {
    /** The (closed-state) trigger's bounding rect. */
    triggerRect: DOMRect;
    /** `window.innerHeight` at measure time. */
    viewportHeight: number;
    /** `window.innerWidth` at measure time. */
    viewportWidth: number;
    /** Estimated popover height — decides whether there's room below or we flip up. */
    estimatedHeight: number;
    /**
     * The panel's natural width — what it would render at if nothing constrained
     * it. A panel sized to its field passes the trigger's width; one with its own
     * `max-w-*` passes that number. Used for the clamp and the `maxWidth` cap.
     */
    panelWidth: number;
    /**
     * Which trigger edge the panel prefers to hang from. `'left'` (default) means
     * it extends rightward; `'right'` means its right edge aligns with the
     * trigger's. A preference only — the clamp overrides it rather than letting
     * the panel leave the screen, so a caller no longer needs its own
     * does-it-fit-this-way branch.
     */
    align?: 'left' | 'right';
    /** Gap between the trigger and the popover, px. Default 4. */
    gap?: number;
    /** Viewport-edge margin kept clear when deciding to flip VERTICALLY, px. Default 8. */
    margin?: number;
    /**
     * Side-edge margin — the safe zone. Default {@link POPOVER_SAFE_ZONE} (16).
     * Deliberately a separate number from `margin`: that one only feeds the
     * flip-up decision, while this one is a hard floor on where the panel may be
     * painted.
     */
    safeZone?: number;
    /**
     * Whether the panel may flip ABOVE the trigger when room below is tight.
     * Default `true`. A caller passes `false` when its menu belongs to a surface
     * the eye reads top-down and an upward flip would cover the thing it came
     * from — the phone auction-detail "More" tab, whose menu flipping up over
     * the header read as a broken control (BL-1857). With `false` the panel
     * always anchors below and `availableHeight` clamps it into a scroll list.
     */
    flip?: boolean;
}

/**
 * Compute a portaled popover's viewport-fixed box from its trigger.
 *
 * Vertically: anchors BELOW the trigger by default; flips to anchor ABOVE (by the
 * popover's bottom edge, so a short menu hugs the field) only when room below is
 * tighter than `estimatedHeight` AND there's more room above than below.
 *
 * Horizontally: hangs from the `align` edge, then clamps so the panel keeps
 * `safeZone` clear of both side edges, and reports the room it has via
 * `maxWidth` so a panel wider than the safe area is narrowed rather than clipped.
 *
 * @param input - {@link ComputePopoverBoxInput}.
 * @returns The {@link PopoverBox} — exactly one of `top` / `bottom` is set.
 */
export function computePopoverBox({
    triggerRect,
    viewportHeight,
    viewportWidth,
    estimatedHeight,
    panelWidth,
    align = 'left',
    gap = 4,
    margin = 8,
    safeZone = POPOVER_SAFE_ZONE,
    flip = true,
}: ComputePopoverBoxInput): PopoverBox {
    const width = Math.round(triggerRect.width);

    // Hang the panel from the preferred edge, then clamp it between the two safe
    // zones. `maxLeft` is the rightmost the panel may start and still keep its
    // right zone clear.
    //
    // THE `Math.max(safeZone, …)` ON `maxLeft` IS THE LOAD-BEARING LINE — it is
    // what makes a panel WIDER than the safe area land on the left zone instead
    // of off the screen, and dropping it reproduces BUG-1625 exactly. The status
    // menu's 384px panel on a 390px phone gives `390 - 16 - 384 = -10`; without
    // the floor, `left` follows that negative number and every section heading
    // loses its first character, which is the reported symptom.
    //
    // A first draft of this function ALSO narrowed the panel to the safe area
    // here, before placing it. That step was deleted: the two floors already
    // absorb every over-wide case, so it changed no output for any input and was
    // dead code (sharing.md § 4). Narrowing still happens — through `maxWidth`,
    // which the consumer applies as the panel's ceiling — just not here.
    const preferredLeft = align === 'right' ? triggerRect.right - panelWidth : triggerRect.left;
    const maxLeft = Math.max(safeZone, viewportWidth - safeZone - panelWidth);
    const left = Math.min(Math.max(safeZone, preferredLeft), maxLeft);

    // The room from where the panel starts to the right safe edge — see the
    // field's doc for why this is measured rather than derived from `panelWidth`.
    // Floored at 0: a viewport narrower than two safe zones (a test harness, a
    // measurement taken mid-transition) would otherwise yield a negative CSS
    // max-width, which is invalid and silently ignored.
    const maxWidth = Math.max(0, viewportWidth - safeZone - left);

    const spaceBelow = viewportHeight - triggerRect.bottom - margin;
    const spaceAbove = triggerRect.top - margin;
    // Flip up only when allowed, below is too tight, AND above has more room.
    const flipUp = flip && spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
    if (flipUp) {
        return {
            left,
            width,
            maxWidth,
            bottom: viewportHeight - triggerRect.top + gap,
            availableHeight: spaceAbove,
        };
    }
    return { left, width, maxWidth, top: triggerRect.bottom + gap, availableHeight: spaceBelow };
}
